
/**
 * DetailsDisclosure 类
 * 这个类用于创建一个自定义的 HTML 元素，特别适用于管理 <details> 元素的展开和收起行为。
 * 它主要用于控制与 <details> 和 <summary> 元素相关的动画，并处理用户与这些元素的交互。
 * 当 <details> 元素的状态改变（展开或收起）时，它会相应地播放或取消内容区域的动画。
 * 这个类还提供了一个方法来手动关闭 <details> 元素，确保可访问性和一致的状态管理。
 */
class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');
    this.content = this.mainDetailsToggle.querySelector('summary').nextElementSibling;
    if(!this.mainDetailsToggle || !this.content) return;

    this.observeVisibility();
  }

  disconnectedCallback() {
    if(this.observer) this.observer.disconnect();
  }

  /**
   * 监听元素可见性
   * 可见的时候执行一操作
   */
  observeVisibility() {
    this.observer = new IntersectionObserver((entries,observer)=>{
      entries.forEach(entry=>{
        if(entry.isIntersecting) {
          // 如果默认是展开的，初始化高度
          this.handleWhenVisibility();
          observer.unobserve(this);
        }
      })
    }, {
      root: null,
      threshold: 0.1
    });

    this.observer.observe(this);
  }

  /**
   * 当元素可见的时候执行
   */
  handleWhenVisibility() {
    if(this.mainDetailsToggle.hasAttribute('open')) this.onToggle();

    this.mainDetailsToggle.addEventListener('toggle', this.onToggle.bind(this));
  }

  onToggle() {
    this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', this.mainDetailsToggle.hasAttribute('open'));

    // 异步添加防止动画未应用
    setTimeout(()=>{
      this.mainDetailsToggle.classList.toggle('has-opened', this.mainDetailsToggle.hasAttribute('open'));
    });
  }

  open() {
    this.mainDetailsToggle.setAttribute('open', 'true');
  }

  close() {
    this.mainDetailsToggle.removeAttribute('open');
  }
}

customElements.define('details-disclosure', DetailsDisclosure);

    /**
 * DropMenu 类
 * 这个类继承自 DetailsDisclosure，提供了特定于下拉菜单和选择器的功能。
 * 除了继承自基类的展开和收起行为的管理外，它还添加了焦点管理的特性。
 * 当用户将焦点从该元素移出时，DropMenu 类会自动关闭 <details> 元素。
 * 支持鼠标悬停打开
 * 还可以根据指定容器的大小自动调整宽度，防止超出或者被隐藏。
 * 适用于页眉【下拉菜单】、【地区和语言选择器】等需要焦点控制和动画支持的交互元素。
 */
class HeaderMenu extends DetailsDisclosure {
  handleWhenVisibility() {
    super.handleWhenVisibility();
    this.mainDetailsToggle.addEventListener('focusout', this.onFocusOut.bind(this));

    // 鼠标悬停打开
    if(this.hasAttribute('data-hover-open')) {
      this.mainDetailsToggle.addEventListener('mouseenter', this.open.bind(this));
      this.mainDetailsToggle.addEventListener('mouseleave', this.close.bind(this))
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onToggle() {
    // 调整位置，防止超出屏幕区域
    if(this.mainDetailsToggle.hasAttribute('data-constrain')) this.adjustContentPosition();

    // 切换 header 激活状态
    const header = this.closest('.section-header');
    // if(header) header.classList.toggle('header--is-active', this.mainDetailsToggle.hasAttribute('open'));
    if (this.mainDetailsToggle.classList.contains('mega-menu')) {
    if(header) header.classList.toggle('header--is-active', this.mainDetailsToggle.hasAttribute('open'));
    }
    super.onToggle();
  }

  /**
   * 调整内容位置，使其不能超过【右侧】限制区域
   * 默认是视口屏幕区域
   * data-area 可设置自定义边界区域
   * data-adjust 自动调整位置
   */
  adjustContentPosition() {
    const needAdjust = this.mainDetailsToggle.hasAttribute('data-adjust'); // 是否需要自动调整位置

    if(!this.mainDetailsToggle.hasAttribute('open')) {
      this.mainDetailsToggle.classList.remove('position--exceed');

      // 自动调整位置，重置位置
      if(needAdjust) {
        this.content.style.left = null;
      }
    } else {
      // 调整位置，使其不能超过限制区域
      let areaRightSidePosition = document.documentElement.clientWidth; // 右边界，默认屏幕右侧，不包含滚动条
      let areaLeftSidePosition = 0; // 左边界，默认屏幕左侧
      if(this.dataset.area) {
        // 如果设置了自定义边界
        const areaRect = document.querySelector(this.dataset.area)?.getBoundingClientRect();
        if(areaRect) {
          areaRightSidePosition = areaRect.right;
          areaLeftSidePosition = areaRect.left;
        }
      }

      const contentRect = this.content.getBoundingClientRect();
      // 偏移量
      let offset = contentRect.right - areaRightSidePosition;

      if(offset > 0) {
        // 超出区域
        this.mainDetailsToggle.classList.add('position--exceed');

        if(needAdjust) {
          this.content.style.left = `${-Math.ceil(offset)}px`;
        }
      }
    }

    this.mainDetailsToggle.classList.toggle('position--constrained', this.mainDetailsToggle.hasAttribute('open'));
  }
}

customElements.define('header-menu', HeaderMenu);